/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import Component from '@glimmer/component';
import {
  HdsTextDisplay,
  HdsTextBody,
  HdsLinkStandalone,
} from '@hashicorp/design-system-components/components';
import './title-row.scss';
import { on } from '@ember/modifier';

import { service } from '@ember/service';
import type ReportingAnalyticsService from '../../../services/reporting-analytics';
import type { SafeString } from '@ember/template';

/**
 * TitleRow Component
 *
 * A reusable component that displays a title with an optional description and link.
 * Used in dashboard cards to create consistent header styling.
 */
export interface TitleRowSignature {
  Args: {
    /** The main title text to display */
    title: string;
    /** Optional description text to display beneath the title */
    description?: string | SafeString;
    /** Custom text for the link (defaults to "View all") */
    linkText?: string;
    /** Icon to display with the link (defaults to "arrow-right") */
    linkIcon?: HdsLinkStandalone['icon'];
    /** URL for the link - if not provided, no link will be shown */
    linkUrl?: string;
    /** Target for the link - defaults to "_self" */
    linkTarget?: '_blank' | '_self';
  };

  Blocks: {
    default: [];
  };

  Element: HTMLElement;
}

export default class TitleRow extends Component<TitleRowSignature> {
  @service declare readonly reportingAnalytics: ReportingAnalyticsService;

  get hasLink() {
    return this.args.linkUrl;
  }

  get linkText() {
    return this.args.linkText || 'View all';
  }

  get linkUrl() {
    return this.args.linkUrl || '#';
  }

  get linkIcon() {
    return this.args.linkIcon || 'arrow-right';
  }

  get linkTarget() {
    return this.args.linkTarget || '_self';
  }

  handleLinkClick = () => {
    this.reportingAnalytics.trackEvent(`card_link`, {
      card: this.args.title,
      link: this.linkText,
      target: this.linkTarget,
    });
  };

  <template>
    <div
      class="ssu-title-row"
      data-test-vault-reporting-dashboard-card-title-row
    >
      <div class="ssu-title-row__container">
        <HdsTextDisplay
          data-test-vault-reporting-dashboard-card-title
          @size="300"
        >
          {{@title}}
        </HdsTextDisplay>

        {{#if this.hasLink}}
          <HdsLinkStandalone
            data-test-vault-reporting-dashboard-card-title-link
            class="ssu-title-row__container__link"
            @text={{this.linkText}}
            @href={{this.linkUrl}}
            @icon={{this.linkIcon}}
            target={{this.linkTarget}}
            @iconPosition="trailing"
            {{on "click" this.handleLinkClick}}
          />
        {{/if}}
      </div>

      {{#if @description}}
        <HdsTextBody
          class="ssu-title-row__description"
          data-test-vault-reporting-dashboard-card-description
        >
          {{@description}}
        </HdsTextBody>
      {{/if}}
    </div>
  </template>
}
